import {ImagebProcesser} from "../process";
import {InputImage, ProcessedImage, ProcessedResult} from "../../types";
import * as pako from 'pako'
import {GrayscaleImage} from "../impls/gray";
import {ReverseColorImage} from "../impls/reverse";

export class Pbita extends ImagebProcesser {
  command: string;
  threshold: number;
  compress: boolean
  reverse: boolean

  constructor(options: {
    command: string,
    threshold: number,
    compress?: boolean,
    reverse?: boolean,
  }) {
    super();
    this.threshold = options.threshold;
    this.command = options.command;
    this.compress = options.compress;
    this.reverse = options.reverse;
  }

  process(input: InputImage): ProcessedResult {
    const width = input.width;
    const height = input.height;

    const grayScaleImage = new GrayscaleImage();
    const grayResult = grayScaleImage.process(input);
    if (grayResult.result == null) {
      return new ProcessedResult({
        origin: input,
        message: 'Failed to process gray scale: ' + grayResult.message
      });
    }
    let preprocessCanvas;
    if (this.reverse) {
      const reverseColorImage = new ReverseColorImage();
      const reverseResult = reverseColorImage.process(grayResult.result.data);
      if (reverseResult.result == null) {
        return new ProcessedResult({
          origin: input,
          message: 'Failed to process reverse: ' + reverseResult.message
        });
      }
      preprocessCanvas = reverseResult.result!.data;
    } else {
      preprocessCanvas = grayResult.result!.data;
    }

    let outputBytes = this._topbitimg(preprocessCanvas);
    if (outputBytes == null) {
      return new ProcessedResult({
        origin: input,
        message: 'Failed to process bitimg'
      });
    }
    if (this.compress) {
      outputBytes = pako.deflate(outputBytes, {windowBits: 10});
    }
    return new ProcessedResult({
      origin: input,
      result: new ProcessedImage({
        data: preprocessCanvas,
        width: width,
        height: height,
        bytes: outputBytes
      })
    })
  }

  public _topbitimg(
    inputImage: InputImage
  ): Uint8Array {
    if (this.command == 'tspl' && !this.compress) {
      return this._topbitimgReverse(this._topbitimgRaw(inputImage));
    } else {
      return this._topbitimgRaw(inputImage);
    }
  }

  public _topbitimgRaw(
    inputImage: InputImage
  ): Uint8Array {
    const width = inputImage.width;
    const height = inputImage.height;

    let eWidth = Math.floor((width % 8 === 0) ? (width / 8) : (width / 8 + 1));
    let currentHeight = 0;
    let index = 0;
    const area = height * eWidth;
    const bytes = []; // the bytes length is equals `area`
    for (let b1 = 0; b1 < area; b1++) {
      bytes[b1] = 0;
    }

    while (currentHeight < height) {
      // let rowData = []; // the row data length is image height
      let eightBitIndex = 0;
      for (let x = 0; x < width; x++) {
        eightBitIndex++;
        if (eightBitIndex > 8) {
          eightBitIndex = 1;
          index++;
        }
        const n = 1 << 8 - eightBitIndex;

        // const pixel = ctx.getImageData(x, currentHeight, 1, 1);
        const pixels = this.fastExtractPixel(inputImage, x, currentHeight, 1, 1);
        const pixel = pixels[0];
        // const rgba = pixel.data;
        const red = pixel[0];
        const green = pixel[1];
        const blue = pixel[2];
        if ((red + green + blue) / 3 < (this.threshold ?? 190)) {
          bytes[index] = (bytes[index] | n);
        }
      }
      index = eWidth * (currentHeight + 1);
      currentHeight += 1;
    }

    return new Uint8Array(bytes);
  }

  public _topbitimgReverse(originBytes: Uint8Array): Uint8Array {
    if (originBytes == null) {
      return null;
    }
    const bytes = [];
    for (let i = 0; i < originBytes.length; i++) {
      bytes[i] = ~originBytes[i];
    }
    return new Uint8Array(bytes);
  }

  private fastExtractPixel(inputImage, x, y, w, h) {
    let i, j;
    let result = [];
    let r, g, b, a;
    const data = inputImage.data;

    for (j = 0; j < h; j++) {
      let idx = (x + (y + j) * inputImage.width) * 4;  // get left most byte index for row at y + j
      for (i = 0; i < w; i++) {
        r = data[idx++];
        g = data[idx++];
        b = data[idx++];
        a = data[idx++];
        // do the processing
        result.push([r, g, b, a]);
      }
    }
    return result;
  }


}

